/**
 * Converts raw AuditLog `changes` into clean, human-readable rows for the
 * Audit Logs UI — the same logic the server uses for the scheduled-report
 * digest, so both surfaces read identically.
 *
 * Audit changes often carry ObjectIds or populated subdocuments (the diff
 * compares an unpopulated old doc to a populated response). This helper:
 *   - skips spurious reference "changes" where the underlying id is unchanged,
 *   - prefers the readable denormalized `*Name` sibling change when present,
 *   - formats amounts / booleans and hides internal/noise fields.
 */

export const FIELD_LABELS: Record<string, string> = {
  amount: 'Amount',
  ref: 'Reference',
  name: 'Name',
  status: 'Status',
  balance: 'Balance',
  accountNumber: 'Account Number',
  donorName: 'Donor',
  donorId: 'Donor ID',
  donorAddress: 'Address',
  donorFatherOrHusbandName: 'Father / Husband Name',
  donorRelation: 'Relation',
  donationTypeId: 'Donation Type',
  donationTypeName: 'Donation Type',
  donorOfficeId: 'Office',
  donorOfficeName: 'Office',
  cashierId: 'Received By',
  cashierName: 'Received By',
  expenseTypeId: 'Expense Type',
  expenseTypeName: 'Expense Type',
  accountId: 'Account',
  accountName: 'Account',
  userId: 'Recorded By',
  userName: 'Recorded By',
  description: 'Description',
  isActive: 'Active',
};

interface RefConfig {
  label: string;
  sibling?: string;
}

const REFERENCE_FIELDS: Record<string, RefConfig> = {
  donationTypeId: { label: 'Donation Type', sibling: 'donationTypeName' },
  donorOfficeId: { label: 'Office', sibling: 'donorOfficeName' },
  cashierId: { label: 'Received By', sibling: 'cashierName' },
  expenseTypeId: { label: 'Expense Type', sibling: 'expenseTypeName' },
  accountId: { label: 'Account' },
  userId: { label: 'Recorded By', sibling: 'userName' },
};

const HIDDEN_FIELDS = new Set([
  '_id',
  '__v',
  'hash',
  'prevHash',
  'verificationCode',
  'pcInfo',
  'printerName',
  'printedAt',
  'createdAt',
  'updatedAt',
  'donorOfficeNo',
  'cashierRole',
  'userRole',
  'donorType',
  'password',
  'token',
  'secret',
]);

const CURRENCY_FIELDS = new Set(['amount', 'balance', 'totalAmount', 'openingBalance']);

const EMPTY = '—';

export interface FormattedChange {
  label: string;
  oldValue: string;
  newValue: string;
}

interface RawChange {
  field: string;
  oldValue: any;
  newValue: any;
}

const formatCurrency = (value: unknown): string => {
  const num = Number(value);
  if (!Number.isFinite(num)) return EMPTY;
  return new Intl.NumberFormat('en-PK', {
    style: 'currency',
    currency: 'PKR',
    maximumFractionDigits: 0,
  }).format(num);
};

const idOf = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (obj._id !== undefined && obj._id !== null) return String(obj._id);
    if (obj.id !== undefined && obj.id !== null) return String(obj.id);
    return String(value);
  }
  return String(value);
};

const nameOf = (value: unknown): string | null => {
  if (!value || typeof value !== 'object') return null;
  const obj = value as Record<string, any>;
  if (typeof obj.name === 'string' && obj.name.trim()) return obj.name.trim();
  if (typeof obj.fullName === 'string' && obj.fullName.trim()) return obj.fullName.trim();
  if (obj.firstName || obj.lastName) return `${obj.firstName ?? ''} ${obj.lastName ?? ''}`.trim();
  return null;
};

const prettifyKey = (key: string): string => {
  const withSpaces = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/Id$/, '')
    .replace(/_/g, ' ')
    .trim();
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
};

const formatScalar = (field: string, value: unknown): string => {
  if (value === null || value === undefined || value === '') return EMPTY;
  if (CURRENCY_FIELDS.has(field)) return formatCurrency(value);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return nameOf(value) ?? EMPTY;
  return String(value);
};

/** Build clean rows from a raw audit log's `changes` + `entityData`. */
export const formatAuditChanges = (
  changes: RawChange[] | undefined,
  entityData?: Record<string, any> | null
): FormattedChange[] => {
  if (!Array.isArray(changes) || changes.length === 0) return [];

  const byField = new Map<string, RawChange>();
  for (const change of changes) {
    if (change && typeof change.field === 'string') byField.set(change.field, change);
  }

  const rows: FormattedChange[] = [];
  const seenLabels = new Set<string>();
  const consumed = new Set<string>();

  const pushRow = (label: string, oldValue: string, newValue: string) => {
    if (seenLabels.has(label) || oldValue === newValue) return;
    seenLabels.add(label);
    rows.push({ label, oldValue, newValue });
  };

  // Phase 1 — reference id fields.
  for (const [field, cfg] of Object.entries(REFERENCE_FIELDS)) {
    const change = byField.get(field);
    if (!change) continue;
    consumed.add(field);

    const oldId = idOf(change.oldValue);
    const newId = idOf(change.newValue);
    if (oldId && newId && oldId === newId) {
      if (cfg.sibling) consumed.add(cfg.sibling);
      continue;
    }

    const siblingChange = cfg.sibling ? byField.get(cfg.sibling) : undefined;
    if (cfg.sibling) consumed.add(cfg.sibling);

    const oldDisplay =
      (siblingChange && siblingChange.oldValue != null && String(siblingChange.oldValue)) || EMPTY;
    const newDisplay =
      (siblingChange && siblingChange.newValue != null && String(siblingChange.newValue)) ||
      (cfg.sibling && entityData?.[cfg.sibling] != null && String(entityData[cfg.sibling])) ||
      nameOf(change.newValue) ||
      EMPTY;

    pushRow(cfg.label, oldDisplay, newDisplay);
  }

  // Phase 2 — remaining scalar / orphan-name fields.
  for (const change of changes) {
    const field = change.field;
    if (!field || consumed.has(field) || HIDDEN_FIELDS.has(field)) continue;
    const label = FIELD_LABELS[field] || prettifyKey(field);
    pushRow(label, formatScalar(field, change.oldValue), formatScalar(field, change.newValue));
  }

  return rows;
};
